import React from 'react';
import { MapPinIcon, FileTextIcon, AlertCircleIcon, CreditCardIcon, SearchIcon, PlusIcon, FileCheckIcon, DownloadIcon, ShieldCheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { samplePlots, sampleApplications } from '../data/sampleData';
export function DashboardPage({
  navigateTo,
  setSelectedPlotId
}: {
  navigateTo: (page: string) => void;
  setSelectedPlotId: (id: string) => void;
}) {
  const {
    language,
    t
  } = useLanguage();
  const stats = [{
    label: t('dashLinkedPlots'),
    value: samplePlots.length,
    icon: MapPinIcon,
    color: 'bg-blue-100 text-blue-600'
  }, {
    label: t('dashActiveCLOs'),
    value: samplePlots.filter(p => p.cloId).length,
    icon: FileCheckIcon,
    color: 'bg-green-100 text-green-600'
  }, {
    label: t('dashPendingMutation'),
    value: samplePlots.filter(p => p.mutationStatus === 'pending').length,
    icon: FileTextIcon,
    color: 'bg-amber-100 text-amber-600'
  }, {
    label: t('dashTaxDue'),
    value: samplePlots.filter(p => p.taxStatus === 'due').length,
    icon: CreditCardIcon,
    color: 'bg-red-100 text-red-600'
  }, {
    label: t('dashDisputed'),
    value: samplePlots.filter(p => p.dispute).length,
    icon: AlertCircleIcon,
    color: 'bg-purple-100 text-purple-600'
  }];
  const quickActions = [{
    label: t('search'),
    icon: SearchIcon,
    action: () => navigateTo('search')
  }, {
    label: t('createRecord'),
    icon: PlusIcon,
    action: () => navigateTo('createRecord')
  }, {
    label: t('applyMutation'),
    icon: FileTextIcon,
    action: () => navigateTo('services')
  }, {
    label: t('payTax'),
    icon: CreditCardIcon,
    action: () => navigateTo('services')
  }, {
    label: t('verifyCLO'),
    icon: ShieldCheckIcon,
    action: () => navigateTo('cloVerification')
  }];
  return <div className="space-y-6" data-id="element-295">
      <div className="flex justify-between items-center" data-id="element-296">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-297">
          {t('navDashboard')}
        </h1>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-298">
        {stats.map((stat, idx) => <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center justify-center text-center cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigateTo('myPlots')} data-id="element-299">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${stat.color}`} data-id="element-300">
              <stat.icon className="w-6 h-6" data-id="element-301" />
            </div>
            <span className="text-2xl font-bold text-gray-900 mb-1" data-id="element-302">
              {stat.value}
            </span>
            <span className="text-xs font-medium text-gray-500" data-id="element-303">
              {stat.label}
            </span>
          </div>)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-304">
        {/* Main Content Area */}
        <div className="lg:col-span-2 space-y-6" data-id="element-305">
          {/* Quick Actions */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-306">
            <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-307">
              {t('dashQuickActions')}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-id="element-308">
              {quickActions.map((action, idx) => <button key={idx} onClick={action.action} className="flex flex-col items-center justify-center p-3 rounded-lg border border-gray-200 hover:border-[#006A4E] hover:bg-green-50 transition-colors group" data-id="element-309">
                  <action.icon className="w-6 h-6 text-gray-500 group-hover:text-[#006A4E] mb-2" data-id="element-310" />
                  <span className="text-xs font-medium text-gray-700 text-center" data-id="element-311">
                    {action.label}
                  </span>
                </button>)}
            </div>
          </div>

          {/* Recent Applications */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-312">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-313">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-314">
                {t('dashRecentApps')}
              </h2>
              <button onClick={() => navigateTo('services')} className="text-sm text-[#006A4E] font-medium hover:underline" data-id="element-315">
                View All
              </button>
            </div>
            <div className="divide-y divide-gray-100" data-id="element-316">
              {sampleApplications.map(app => <div key={app.id} className="p-4 hover:bg-gray-50 flex items-center justify-between cursor-pointer" onClick={() => {
              setSelectedPlotId(app.plotId);
              navigateTo('plotDetails');
            }} data-id="element-317">
                  <div className="flex items-center space-x-4" data-id="element-318">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center" data-id="element-319">
                      <FileTextIcon className="w-5 h-5 text-gray-600" data-id="element-320" />
                    </div>
                    <div data-id="element-321">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-322">
                        {language === 'bn' ? app.typeBn : app.type}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-323">
                        {app.id} • {app.plotId}
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-324">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${app.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-325">
                      {language === 'bn' ? app.status === 'completed' ? 'সম্পন্ন' : 'অপেক্ষমাণ' : app.status}
                    </span>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-326">{app.date}</p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* Sidebar Area */}
        <div className="space-y-6" data-id="element-327">
          {/* Mini Map Widget */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col h-[300px]" data-id="element-328">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-white z-10" data-id="element-329">
              <h2 className="text-sm font-bold text-gray-900" data-id="element-330">{t('navMap')}</h2>
              <button onClick={() => navigateTo('map')} className="text-xs text-[#006A4E] font-medium hover:underline" data-id="element-331">
                Expand
              </button>
            </div>
            <div className="flex-1 bg-green-50 relative cursor-pointer" onClick={() => navigateTo('map')} data-id="element-332">
              {/* Simulated Map Background */}
              <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: 'radial-gradient(#006A4E 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }} data-id="element-333"></div>
              {samplePlots.map(plot => <div key={plot.id} className="absolute w-4 h-4 bg-[#006A4E] rounded-full border-2 border-white shadow-md transform -translate-x-1/2 -translate-y-1/2" style={{
              left: plot.mapCoords.x,
              top: plot.mapCoords.y
            }} data-id="element-334" />)}
            </div>
          </div>

          {/* Report Summary */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigateTo('reports')} data-id="element-335">
            <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-336">
              {t('dashReportSummary')}
            </h2>
            <div className="space-y-4" data-id="element-337">
              <div data-id="element-338">
                <div className="flex justify-between text-sm mb-1" data-id="element-339">
                  <span className="text-gray-600" data-id="element-340">
                    {language === 'bn' ? 'কৃষি জমি' : 'Agricultural Land'}
                  </span>
                  <span className="font-medium" data-id="element-341">45%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-342">
                  <div className="bg-[#006A4E] h-2 rounded-full" style={{
                  width: '45%'
                }} data-id="element-343"></div>
                </div>
              </div>
              <div data-id="element-344">
                <div className="flex justify-between text-sm mb-1" data-id="element-345">
                  <span className="text-gray-600" data-id="element-346">
                    {language === 'bn' ? 'আবাসিক জমি' : 'Residential Land'}
                  </span>
                  <span className="font-medium" data-id="element-347">35%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-348">
                  <div className="bg-blue-500 h-2 rounded-full" style={{
                  width: '35%'
                }} data-id="element-349"></div>
                </div>
              </div>
              <div data-id="element-350">
                <div className="flex justify-between text-sm mb-1" data-id="element-351">
                  <span className="text-gray-600" data-id="element-352">
                    {language === 'bn' ? 'বাণিজ্যিক জমি' : 'Commercial Land'}
                  </span>
                  <span className="font-medium" data-id="element-353">20%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-354">
                  <div className="bg-[#C9A84C] h-2 rounded-full" style={{
                  width: '20%'
                }} data-id="element-355"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}